import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators';
import * as url from 'url';

import { environment } from '../../environments/environment';
import { Constants } from "./const";
import * as Api from './itsr-api.model';
import { SessionStorageService } from "./sessionstorage.service";

export interface ErrorShape {
  Key?: string;
  Code?: number;
  Message: string;
}

export interface ItsrApiErrorResponse {
  Message: string;
  HttpCode: number;
  Errors: ErrorShape[];
}

export type ItsrApiResponse<T> = HttpResponse<T> | ItsrApiErrorResponse;

@Injectable()
export class ItsrApiService {
  // API endpoints relative to the base URL
  static readonly UrlLogin = 'account/login';
  static readonly UrlRefreshToken = 'account/token';
  static readonly UrlLogout = 'account/logout';
  static readonly UrlActivate = 'account/activate';
  static readonly UrlProfile = 'account/profile';
  static readonly UrlContactInfo = 'account/changeContact';
  static readonly UrlGetContactInfo = 'account/contact';
  static readonly UrlSecurityQuestionList = 'account/questions';
  static readonly UrlSecurityQuestion = 'account/security/question';
  static readonly UrlUserSecurityQuestion = 'account/valid/username';
  static readonly UrlForgotPassword = 'account/changePasswordByToken';
  static readonly UrlForgotPasswordEmail = 'account/valid/securityanswer/email';
  static readonly UrlChangePassword = 'account/changepassword';
  static readonly UrlEmailInquiry = 'account/email/inquire';
  static readonly UrlReimbursementAllowanceList = 'reimbursement/allowance';
  static readonly UrlReimbursementRecordList = 'reimbursement/records';
  static readonly UrlReimbursementStatusList = 'reimbursement/status';
  static readonly UrlReimbursementResourceKey = 'reimbursement/resourcekey';
  static readonly UrlReimbursementAttachment = 'reimbursement/attachment';
  static readonly UrlHomeInspectionReimbursement = 'reimbursement/home';
  static readonly UrlServiceGuaranteeReimbursement = 'reimbursement/service';
  static readonly UrlCashBackReimbursement = 'reimbursement/cash';

  // standard HTTP header names
  static readonly HeaderAccept = 'Accept';
  static readonly HeaderAcceptLanguage = 'Accept-Language';
  static readonly HeaderAuthorization = 'Authorization';
  static readonly HeaderContentType = 'Content-Type';

  // custom HTTP header names
  static readonly HeaderClientId = 'client_id';
  static readonly HeaderInstallationId = 'installation_id';
  static readonly HeaderResourceKey = 'ResourceKey';
  static readonly HeaderSiteId = 'SiteID';
  static readonly HeaderSiteUrl = 'SiteURL';
  static readonly HeaderCookie = 'Cookie';

  // default request header values
  static readonly DefaultAccept = 'application/json';
  static readonly DefaultContentType = 'application/json'; // only for POST requests
  static readonly DefaultClientId = 'IP360UI';
  static readonly DefaultInstallationId = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9';
  static readonly DefaultSiteId = '2001';
  static readonly DefaultSiteUrl = 'www.careformyhome.com';
  static readonly DefaultCookie = 'ARRAffinity=ea33ae04a13ceb63aea4386a86343d9f6eece28c82c714f0b61dfe23d9a53905; ARRAffinitySameSite=ea33ae04a13ceb63aea4386a86343d9f6eece28c82c714f0b61dfe23d9a53905';

  // default request headers
  static readonly DefaultRequestHeaders = {
    [ItsrApiService.HeaderAccept]: ItsrApiService.DefaultAccept,
    [ItsrApiService.HeaderAcceptLanguage]: Constants.DefaultLanguageCode,
    [ItsrApiService.HeaderClientId]: ItsrApiService.DefaultClientId,
    [ItsrApiService.HeaderInstallationId]: ItsrApiService.DefaultInstallationId,
    [ItsrApiService.HeaderSiteId]: ItsrApiService.DefaultSiteId,
    [ItsrApiService.HeaderSiteUrl]: ItsrApiService.DefaultSiteUrl,
    [ItsrApiService.HeaderSiteUrl]: ItsrApiService.DefaultSiteUrl,
    [ItsrApiService.HeaderCookie]: ItsrApiService.DefaultCookie,
  };

  constructor(private httpClient: HttpClient, private sessionState: SessionStorageService) {
    this.handleError = this.handleError.bind(this);
  }

  static isErrorResponse(obj: any): obj is ItsrApiErrorResponse {
    return typeof obj === 'object' &&
      typeof obj.Message === 'string' &&
      typeof obj.HttpCode === 'number' &&
      Array.isArray(obj.Errors);
  }

  static resolveUrl(relativeUrl: string): string {
    return url.resolve(Constants.ItsrApiBaseUrl, relativeUrl);
  }

  getDefaultRequestHeaders(authToken?: string, contentType?: string): HttpHeaders {
    return new HttpHeaders(Object.assign({},
      ItsrApiService.DefaultRequestHeaders,
      { [ItsrApiService.HeaderAcceptLanguage]: this.sessionState.getCurrentLanguageCode() },
      authToken ? { [ItsrApiService.HeaderAuthorization]: `Bearer ${authToken}` } : null,
      contentType ? { [ItsrApiService.HeaderContentType]: contentType } : null));
  }

  get<T>(relativeUrl: string, authToken?: string, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    const apiUrl = ItsrApiService.resolveUrl(relativeUrl);
    return this.httpClient.get<T>(apiUrl, {
      headers: headers || this.getDefaultRequestHeaders(authToken),
      observe: 'response'
    })
      .pipe(catchError(this.handleError));
  }

  // convenience method to auto set Authorization header before issuing a GET request
  authGet<T>(relativeUrl: string, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    return this.get<T>(relativeUrl, this.sessionState.getAuthToken(), headers);
  }

  post<T>(relativeUrl: string, body: any, authToken?: string, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    const apiUrl = ItsrApiService.resolveUrl(relativeUrl);
    return this.httpClient.post<T>(apiUrl, body, {
      headers: headers || this.getDefaultRequestHeaders(authToken, ItsrApiService.DefaultContentType),
      observe: 'response'
    })
      .pipe(catchError(this.handleError));
  }

  // convenience method to auto set Authorization header before issuing a POST request
  authPost<T>(relativeUrl: string, body: any, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    return this.post<T>(relativeUrl, body, this.sessionState.getAuthToken(), headers);
  }

  put<T>(relativeUrl: string, body: any, authToken?: string, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    const apiUrl = ItsrApiService.resolveUrl(relativeUrl);
    return this.httpClient.put<T>(apiUrl, body, {
      headers: headers || this.getDefaultRequestHeaders(authToken, ItsrApiService.DefaultContentType),
      observe: 'response'
    })
      .pipe(catchError(this.handleError));
  }

  // convenience method to auto set Authorization header before issuing a PUT request
  authPut<T>(relativeUrl: string, body: any, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    return this.put<T>(relativeUrl, body, this.sessionState.getAuthToken(), headers);
  }

  patch<T>(relativeUrl: string, body: any, authToken?: string, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    const apiUrl = ItsrApiService.resolveUrl(relativeUrl);
    return this.httpClient.patch<T>(apiUrl, body, {
      headers: headers || this.getDefaultRequestHeaders(authToken, ItsrApiService.DefaultContentType),
      observe: 'response'
    })
      .pipe(catchError(this.handleError));
  }

  // convenience method to auto set Authorization header before issuing a PATCH request
  authPatch<T>(relativeUrl: string, body: any, headers?: HttpHeaders): Observable<ItsrApiResponse<T>> {
    return this.patch<T>(relativeUrl, body, this.sessionState.getAuthToken(), headers);
  }

  public handleError(err: HttpErrorResponse): Observable<ItsrApiErrorResponse> {
    const errorResponse = err.error;
    const errorMessage: string = errorResponse.message || errorResponse.Message || Constants.getGenericErrorMessage();
    const errors: ErrorShape[] = errorResponse.Errors || [{ Message: errorMessage }];

    if (!environment.production) {
      const lang = this.sessionState.getCurrentLanguageCode();
      errors.forEach((error: ErrorShape) => {
        if (error.Code > 0) {
          const errorMessagePrefix = lang === Constants.LanguageCodeFrench
            ? `Erreur ${error.Code} : `
            : `Error ${error.Code}: `;
          error.Message = `${errorMessagePrefix} ${error.Message}`;
        }
      });
    }

    return of(<ItsrApiErrorResponse>{ Message: errorMessage, HttpCode: err.status, Errors: errors });
  }

  getJwtToken(userName?: string, password?: string): Observable<ItsrApiResponse<Api.AccountLoginResponse>> {
    return this.post<Api.AccountLoginResponse>(ItsrApiService.UrlLogin, { userName, password });
  }

  logout(): Observable<ItsrApiResponse<Api.AccountLoginResponse>> {
    return this.post<Api.AccountLoginResponse>(ItsrApiService.UrlLogout, { });
  }

  postActivateUser(requestBody: any): Observable<ItsrApiResponse<boolean>> {
    return this.post<boolean>(ItsrApiService.UrlActivate, requestBody);
  }

  getAccountProfile(): Observable<ItsrApiResponse<Api.AccountProfileResponse>> {
    return this.authGet<Api.AccountProfileResponse>(ItsrApiService.UrlProfile);
  }

  getContactInfo(): Observable<ItsrApiResponse<Api.AccountContactInfoResponse>> {
    return this.authGet<Api.AccountContactInfoResponse>(ItsrApiService.UrlGetContactInfo);
  }

  putContactInfo(requestBody: any): Observable<ItsrApiResponse<boolean>> {
    return this.authPut<boolean>(ItsrApiService.UrlContactInfo, requestBody);
  }

  getSecurityQuestionList(): Observable<ItsrApiResponse<Api.SecurityQuestionListResponse>> {
    return this.post<Api.SecurityQuestionListResponse>(ItsrApiService.UrlSecurityQuestionList, '" "');
  }

  getUserSecurityQuestion(username: string): Observable<ItsrApiResponse<Api.UserSecurityQuestionResponse>> {
    return this.post<Api.UserSecurityQuestionResponse>(ItsrApiService.UrlUserSecurityQuestion, `"${username}"`);
  }

  patchSecurityQuestion(requestBody: any): Observable<ItsrApiResponse<boolean>> {
    console.log(requestBody);
    return this.authPatch<boolean>(ItsrApiService.UrlSecurityQuestion, requestBody);
  }

  postForgotPassword(securityCode: string,
    username: string, password: string, confirmPassword: string): Observable<ItsrApiResponse<boolean>> {

    return this.put(ItsrApiService.UrlForgotPassword, {
      Username:username,
      SecurityToken:securityCode,
      NewPassword:password,
      ConfirmPassword: confirmPassword
    });
  }

  postForgotPasswordEmail(requestBody: any): Observable<ItsrApiResponse<boolean>> {
    return this.post<boolean>(ItsrApiService.UrlForgotPasswordEmail, requestBody);
  }

  // tslint:disable-next-line:max-line-length
  putChangePassword(username,oldPassword: string, newPassword: string, confirmPassword: string): Observable<ItsrApiResponse<boolean>> {
    return this.authPut<boolean>(ItsrApiService.UrlChangePassword, { username, oldPassword, newPassword, confirmPassword });
  }

  postEmailInquiry(requestBody: any): Observable<ItsrApiResponse<boolean>> {
    return this.authPost(ItsrApiService.UrlEmailInquiry, requestBody);
  }

  getReimbursementAllowanceList(): Observable<ItsrApiResponse<Api.ReimbursementAllowanceListResponse>> {
    return this.authGet<Api.ReimbursementAllowanceListResponse>(ItsrApiService.UrlReimbursementAllowanceList);
  }

  getReimbursementRecordList(): Observable<ItsrApiResponse<Api.ReimbursementRecordListResponse>> {
    return this.authGet<Api.ReimbursementRecordListResponse>(ItsrApiService.UrlReimbursementRecordList);
  }

  getReimbursementStatusList(): Observable<ItsrApiResponse<Api.ReimbursementStatusListResponse>> {
    return this.authGet<Api.ReimbursementStatusListResponse>(ItsrApiService.UrlReimbursementStatusList);
  }

  getReimbursementResourceKey(): Observable<ItsrApiResponse<string>> {
    return this.authGet<string>(ItsrApiService.UrlReimbursementResourceKey);
  }

  postReimbursementAttachment(files: File[], resourceKey: string): Observable<ItsrApiResponse<boolean>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`Receipt ${index + 1}`, file);
    });

    const headers = this.getDefaultRequestHeaders(this.sessionState.getAuthToken())
      .set(ItsrApiService.HeaderResourceKey, resourceKey);
    return this.post<boolean>(ItsrApiService.UrlReimbursementAttachment, formData, undefined, headers);
  }

  postHomeInspectionReimbursement(requestBody: any): Observable<ItsrApiResponse<number>> {
    return this.authPost<number>(ItsrApiService.UrlHomeInspectionReimbursement, requestBody);
  }

  postServiceGuaranteeReimbursement(requestBody: any): Observable<ItsrApiResponse<number>> {
    return this.authPost<number>(ItsrApiService.UrlServiceGuaranteeReimbursement, requestBody);
  }

  postCashBackReimbursement(requestBody: any): Observable<ItsrApiResponse<number>> {
    return this.authPost<number>(ItsrApiService.UrlCashBackReimbursement, requestBody);
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/itsr-api.service.ts